import {
  MOBILE_LINK_HOST,
  MOBILE_LINK_TENANT_PATH,
} from "../constants/mobile-link";
import type {
  IAnalyticsEventEnvelope,
  IRedactAnalyticsEventProps,
  IRedactAnalyticsUrlProps,
  IRedactAnalyticsValueProps,
  IScrubAnalyticsPropertiesProps,
  ISecretPathSegment,
} from "./product-analytics.types";

// NOTE: NSAI-6105 / NSAI-6118 — the single redactor both clients run. Two
// findings drove it, and they are the reason it is shaped this way rather than
// as a tidier "sanitise the path we pass to capture":
//
//   1. Web's `capturePageview` concatenated `location.search`, so a
//      password-reset / invite token reached `$current_url` and `pathname`.
//   2. Both SDKs then republish a route they captured ONCE onto every later
//      event — web as `$session_entry_url`, mobile as the sticky `$screen_name`
//      super property. Sanitising our own call sites leaves those untouched,
//      which is why this also runs as a `before_send` hook: that is the only
//      place an SDK-generated property can be caught.
//
// The tracking plan (`docs/observability/posthog-tracking-plan.md`) forbids
// sending access tokens, API keys and signed URLs at all — this file is where
// that rule is enforced rather than restated.
export const ANALYTICS_REDACTED = "[redacted]";

// NOTE: Query parameters kept verbatim. An ALLOWLIST, not a denylist of
// credential names: a denylist only protects against the parameter names
// someone thought of, and the next credential-bearing parameter will not be on
// it. Attribution is the only thing analytics genuinely needs out of a query
// string, so it is the only thing that survives.
const SAFE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  "gclid",
  "fbclid",
  "ref",
]);

// NOTE: The whole `utm_` family, matched by prefix rather than by an exact list.
// GA4 keeps adding members (`utm_id`, `utm_source_platform`,
// `utm_marketing_tactic`, `utm_creative_format`), and an exact list silently
// drops campaign attribution for every one it has not been updated for. Safe to
// widen this way because the prefix is an analytics convention — nothing mints a
// credential called `utm_*`.
const SAFE_QUERY_PARAM_PREFIX = "utm_";

const SHARE_TOKEN_PLACEHOLDER = "[shareToken]";

// NOTE: Routes whose NEXT segment is a bearer capability, not an id: every
// `:shareToken` route in web's PATHS and every `[shareToken]` screen in the
// mobile app (a test reads both, so a new one cannot be missed silently).
// Replaced with the route template, which is both the safe value and the more
// useful one: screens aggregate instead of fragmenting one row per token.
// NOTE: NSAI-7483 — matched at any segment boundary, not only at the start of
// the path: the link the server builds for a tenant served under a path prefix
// reads `/t/<tenant-host>/<prefix>/shared/public-chat/<token>`.
const SECRET_PATH_SEGMENTS: readonly ISecretPathSegment[] = [
  { routeSegments: ["shared", "chat"], placeholder: SHARE_TOKEN_PLACEHOLDER },
  {
    routeSegments: ["shared", "public-chat"],
    placeholder: SHARE_TOKEN_PLACEHOLDER,
  },
  {
    routeSegments: ["shared", "meeting"],
    placeholder: SHARE_TOKEN_PLACEHOLDER,
  },
  { routeSegments: ["shared", "page"], placeholder: SHARE_TOKEN_PLACEHOLDER },
];

const PATH_SEPARATOR = "/";

// NOTE: NSAI-7483 — `https://go.nodeshift.com/t/<tenant-host>/<path>`, the
// mobile link redirector's route (`parseMobileLink`). The segment after `/t/`
// is the host name of a customer's server, which analytics has no use for.
// Unlike the routes above it is templated only at the start of the path (its
// first non-empty segment), and only on a URL whose host is the redirector or
// whose scheme is an app scheme: `/t/…` on any other host is some other site's
// route and means nothing here.
const TENANT_LINK_SEGMENT: ISecretPathSegment = {
  routeSegments: MOBILE_LINK_TENANT_PATH.split(PATH_SEPARATOR).filter(
    (segment) => segment.length > 0,
  ),
  placeholder: "[tenantHost]",
};

// NOTE: NSAI-7483 — a URL: a scheme followed by at least one "/", as in
// `https://host/path`, `nsai-app://shared/…` and `nsai-app:/shared/…`.
// Anything else is treated as a bare path, which is what both providers pass;
// so is a scheme-relative `//host/path`, whose share routes are still
// templated because those match anywhere. A scheme with no slash at all
// (`nsai-app:shared/chat/<token>`, which expo-router also routes) is a known
// gap: `word:text` is too common in values that are not URLs to be read as a
// route without knowing the app's own scheme.
const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:(?=\/)/i;

// NOTE: NSAI-7483 — the only URLs whose authority is a host. Every other
// scheme (the app's own `nsai-app:`, whatever opened it) is read the way
// expo-router roughly reads it: everything after the scheme is the route, so
// in `nsai-app://shared/chat/<token>` "shared" is the first route segment, not
// a host. expo-router's `extractExactPathFromURL` tests `^https?://` the same
// way, then keeps the host and path a `URL` parser gives it, which the hand
// parsing below approximates rather than reproduces.
const HOST_URL_PATTERN = /^https?:\/\//i;
const AUTHORITY_PREFIX = "//";

// NOTE: User info, everything up to the last "@" of an authority, is a
// credential whenever it is there, so it is dropped rather than templated. In
// an app-scheme URL there is an authority only after exactly two slashes;
// expo-router keeps its host and drops the rest, so
// `nsai-app://x@shared/chat/<token>` opens the viewer.
const USER_INFO_PATTERN = /^[^/]*@/;
const APP_SCHEME_USER_INFO_PATTERN = /^\/\/[^/]*@/;

const LEADING_SLASHES_PATTERN = /^\/+/;
const TRAILING_DOT_PATTERN = /\.$/;
const PORT_SEPARATOR = ":";

const redactQueryString = ({ search }: { search: string }): string => {
  if (!search) return "";

  const redacted = search
    .split("&")
    .filter((pair) => pair.length > 0)
    .map((pair) => {
      const separatorIndex = pair.indexOf("=");
      if (separatorIndex === -1) return pair;

      const name = pair.slice(0, separatorIndex).toLowerCase();
      if (SAFE_QUERY_PARAMS.has(name)) return pair;
      if (name.startsWith(SAFE_QUERY_PARAM_PREFIX)) return pair;

      return `${name}=${ANALYTICS_REDACTED}`;
    })
    .join("&");

  return redacted ? `?${redacted}` : "";
};

// NOTE: NSAI-7483 — a path segment as the routers compare it: React Router
// decodes each segment and matches routes ignoring case, so
// `/%73hared/Page/<token>` opens the viewer too. A segment that does not
// decode is compared as it is.
const toRouteSegment = ({ segment }: { segment: string }): string => {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
};

// NOTE: `filledIndexes` holds the indexes of the non-empty `segments`. When
// the non-empty segments from `position` on are `secretPath`'s route segments
// (compared as `toRouteSegment` reads them) and another non-empty segment
// follows them, replaces that one with the placeholder. Empty segments are
// skipped because expo-router collapses `//` before it routes, so
// `nsai-app://shared/chat//<token>` opens the viewer too. Only the secret
// segment is replaced: anything nested below it is a real route and keeps its
// shape. A placeholder matches no route, so a templated segment is never read
// as the start of another one.
const templateSecretSegment = ({
  segments,
  filledIndexes,
  position,
  secretPath,
}: {
  segments: string[];
  filledIndexes: number[];
  position: number;
  secretPath: ISecretPathSegment;
}): void => {
  const secretPosition = position + secretPath.routeSegments.length;
  if (secretPosition >= filledIndexes.length) return;

  const isRouteMatch = secretPath.routeSegments.every(
    (routeSegment, offset) =>
      toRouteSegment({
        segment: segments[filledIndexes[position + offset]],
      }) === routeSegment,
  );
  if (isRouteMatch) {
    segments[filledIndexes[secretPosition]] = secretPath.placeholder;
  }
};

// NOTE: `isTenantLink` says the path belongs to a URL the `/t/` rule applies
// to (see TENANT_LINK_SEGMENT); a bare path never does, since its host is not
// known. The share routes are templated wherever they start.
const redactPathname = ({
  pathname,
  isTenantLink,
}: {
  pathname: string;
  isTenantLink: boolean;
}): string => {
  const segments = pathname.split(PATH_SEPARATOR);
  const filledIndexes = segments.flatMap((segment, index) =>
    segment ? [index] : [],
  );

  if (isTenantLink) {
    templateSecretSegment({
      segments,
      filledIndexes,
      position: 0,
      secretPath: TENANT_LINK_SEGMENT,
    });
  }

  for (let position = 0; position < filledIndexes.length; position += 1) {
    for (const secretPath of SECRET_PATH_SEGMENTS) {
      templateSecretSegment({ segments, filledIndexes, position, secretPath });
    }
  }

  return segments.join(PATH_SEPARATOR);
};

// NOTE: The redirector is recognised the way a browser would read its host:
// in any case, without a port or the root dot of a fully qualified name.
// Reading it loosely only ever templates more.
const getIsMobileLinkHost = ({
  hostAndPort,
}: {
  hostAndPort: string;
}): boolean => {
  const portIndex = hostAndPort.indexOf(PORT_SEPARATOR);
  const host = portIndex === -1 ? hostAndPort : hostAndPort.slice(0, portIndex);

  return (
    host.replace(TRAILING_DOT_PATTERN, "").toLowerCase() === MOBILE_LINK_HOST
  );
};

// NOTE: `rest` is everything between `://` and the query of an http(s) URL:
// the authority, then the path from its first "/".
const redactHostUrlRest = ({ rest }: { rest: string }): string => {
  const pathStartIndex = rest.indexOf(PATH_SEPARATOR);
  const authority =
    pathStartIndex === -1 ? rest : rest.slice(0, pathStartIndex);
  const pathname = pathStartIndex === -1 ? "" : rest.slice(pathStartIndex);
  const hostAndPort = authority.replace(USER_INFO_PATTERN, "");

  return `${hostAndPort}${redactPathname({
    pathname,
    isTenantLink: getIsMobileLinkHost({ hostAndPort }),
  })}`;
};

// NOTE: NSAI-7483 — `rest` is everything between the scheme's ":" and the
// query of an app-scheme URL, all of it route (see HOST_URL_PATTERN) once any
// user info is dropped. Leading slashes are set aside first, so
// `nsai-app:/shared/chat/<token>`, `nsai-app://shared/chat/<token>` and
// `nsai-app:///shared/chat/<token>` are read as the same route, as expo-router
// reads them, and are each given back with the slashes they came with.
const redactAppSchemeRest = ({ rest }: { rest: string }): string => {
  const withoutUserInfo = rest.replace(
    APP_SCHEME_USER_INFO_PATTERN,
    AUTHORITY_PREFIX,
  );
  const route = withoutUserInfo.replace(LEADING_SLASHES_PATTERN, "");
  const leadingSlashes = withoutUserInfo.slice(
    0,
    withoutUserInfo.length - route.length,
  );
  const redactedRoute = redactPathname({
    pathname: `${PATH_SEPARATOR}${route}`,
    isTenantLink: true,
  });

  return `${leadingSlashes}${redactedRoute.slice(PATH_SEPARATOR.length)}`;
};

// NOTE: Takes an absolute URL or a bare path and returns it with secret path
// segments templated, user info dropped and every non-attribution query value
// redacted. The fragment is dropped outright: nothing in the tracking plan
// needs it, and it is another place a token can ride along.
export const redactAnalyticsUrl = ({
  url,
}: IRedactAnalyticsUrlProps): string => {
  if (!url) return url;

  const fragmentIndex = url.indexOf("#");
  const withoutFragment =
    fragmentIndex === -1 ? url : url.slice(0, fragmentIndex);

  const queryIndex = withoutFragment.indexOf("?");
  const beforeQuery =
    queryIndex === -1 ? withoutFragment : withoutFragment.slice(0, queryIndex);
  const search = queryIndex === -1 ? "" : withoutFragment.slice(queryIndex + 1);
  const redactedQuery = redactQueryString({ search });

  const scheme = URL_SCHEME_PATTERN.exec(beforeQuery)?.[0];
  if (!scheme) {
    return `${redactPathname({ pathname: beforeQuery, isTenantLink: false })}${redactedQuery}`;
  }

  // NOTE: Split scheme, authority and path by hand rather than through `URL`,
  // so this stays dependency-free and behaves the same in Hermes and in a
  // browser.
  const rest = beforeQuery.slice(scheme.length);
  const redactedRest = HOST_URL_PATTERN.test(beforeQuery)
    ? `${AUTHORITY_PREFIX}${redactHostUrlRest({
        rest: rest.slice(AUTHORITY_PREFIX.length),
      })}`
    : redactAppSchemeRest({ rest });

  return `${scheme}${redactedRest}${redactedQuery}`;
};

// NOTE: Whether a value is worth running through the URL redactor. Deliberately
// shape-based rather than a list of known property names: `$session_entry_url`
// was missed precisely because nobody enumerated it, and the next SDK release
// can add another one without telling us.
const getIsLocationLike = ({ value }: { value: string }): boolean =>
  value.startsWith(PATH_SEPARATOR) || URL_SCHEME_PATTERN.test(value);

export const redactAnalyticsValue = ({
  value,
}: IRedactAnalyticsValueProps): string =>
  getIsLocationLike({ value }) ? redactAnalyticsUrl({ url: value }) : value;

// NOTE: NSAI-2433 — property keys that name a person or a credential, compared
// after `normalizePropertyKey`, so `firstName`, `first_name` and `First-Name`
// are one entry. A key-based drop is the second line of defence: the types
// already keep these out of our own calls, but an SDK or a future call site can
// add one without going through them. `devicename` is posthog-react-native's
// `$device_name`, which iOS fills with "<owner>'s iPhone". The SDK's own
// `token` (the public project key) and `distinct_id` are deliberately absent —
// dropping either would get every event rejected.
const PII_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  "email",
  "emailaddress",
  "phone",
  "phonenumber",
  "mobile",
  "mobilenumber",
  "telephone",
  "name",
  "firstname",
  "lastname",
  "fullname",
  "displayname",
  "username",
  "devicename",
  "address",
  "password",
  "accesstoken",
  "refreshtoken",
  "apikey",
  "secret",
]);

// NOTE: Where posthog-js and posthog-react-native put person properties
// (`$set`, `$set_once` on an `$identify`) and group properties (`$group_set` on
// a `$groupidentify`). Scrubbed as nested objects, by the same rules.
const NESTED_PROPERTY_BAGS = ["$set", "$set_once", "$group_set"] as const;

// NOTE: posthog-js also lifts `$set` / `$set_once` beside `properties` on the
// event itself; `$group_set` only ever travels inside `properties`.
const TOP_LEVEL_PROPERTY_BAGS = ["$set", "$set_once"] as const;

// NOTE: An email anywhere inside a value — a path, an error text, a label —
// written plainly or percent-encoded (`%40`), as it is inside a URL the SDK
// reports and inside the `utm_*` values the URL redactor keeps verbatim.
// NOTE: Every part is bounded (RFC 5321 lengths) and the local part, the
// separator and the domain draw on disjoint characters, so matching stays
// linear on a long value: no run can be re-split between two quantifiers.
// A `%XX` escape counts as one local-part character, except `%40`, which is
// the separator. In the domain, an encoded dot (`%2E`) separates labels too.
const EMAIL_PATTERN =
  /(?:[a-z0-9._+-]|%(?!40)[0-9a-f]{2}){1,64}(?:@|%40)[a-z0-9-]{1,63}(?:(?:\.|%2e)[a-z0-9-]{1,63}){1,8}/gi;

// NOTE: A whole value that is only a phone number: digits with the usual
// separators. Matched against the WHOLE value, not a substring, so a uuid or a
// timestamp that happens to hold a run of digits is left alone. Applied to our
// own keys only, never to an SDK `$` property: those are device and library
// metadata, and an Android build number such as `2026100601` is phone-shaped.
const PHONE_SHAPED_PATTERN = /^\+?[\d\s().-]+$/;
const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 15;

// NOTE: `2026-10-06` and `2026-10-06 12:00` are phone-shaped by characters
// alone; dates are what `created_at` carries, so they are excluded explicitly.
const ISO_DATE_PREFIX_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const normalizePropertyKey = ({ key }: { key: string }): string =>
  key.toLowerCase().replace(/[^a-z0-9]/g, "");

const getIsPiiPropertyKey = ({ key }: { key: string }): boolean =>
  PII_PROPERTY_KEYS.has(normalizePropertyKey({ key }));

const getIsPhoneNumber = ({ value }: { value: string }): boolean => {
  const trimmed = value.trim();
  if (!PHONE_SHAPED_PATTERN.test(trimmed)) return false;
  if (ISO_DATE_PREFIX_PATTERN.test(trimmed)) return false;

  const digitCount = trimmed.replace(/\D/g, "").length;
  return digitCount >= PHONE_MIN_DIGITS && digitCount <= PHONE_MAX_DIGITS;
};

const SDK_PROPERTY_PREFIX = "$";

const redactPersonalValue = ({
  key,
  value,
}: {
  key: string;
  value: string;
}): string => {
  if (!key.startsWith(SDK_PROPERTY_PREFIX) && getIsPhoneNumber({ value })) {
    return ANALYTICS_REDACTED;
  }
  return value.replace(EMAIL_PATTERN, ANALYTICS_REDACTED);
};

// NOTE: NSAI-2433 — drops PII-named keys and redacts email / phone values, then
// runs the URL redactor, on one flat property bag. Mutates in place for the
// same reason `redactAnalyticsEvent` does: `before_send` must hand the SDK back
// its own object.
export const scrubAnalyticsProperties = ({
  properties,
}: IScrubAnalyticsPropertiesProps): void => {
  for (const key of Object.keys(properties)) {
    if (getIsPiiPropertyKey({ key })) {
      delete properties[key];
      continue;
    }

    const value = properties[key];
    if (typeof value !== "string") continue;

    const redacted = redactPersonalValue({
      key,
      value: redactAnalyticsValue({ value }),
    });
    if (redacted !== value) {
      properties[key] = redacted;
    }
  }
};

const getIsPropertyBag = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const scrubNestedPropertyBags = ({
  holder,
}: {
  holder: Record<string, unknown>;
}): void => {
  for (const bagKey of NESTED_PROPERTY_BAGS) {
    const bag = holder[bagKey];
    if (getIsPropertyBag(bag)) {
      scrubAnalyticsProperties({ properties: bag });
    }
  }
};

// NOTE: The `before_send` body, kept SDK-agnostic so web and mobile can each
// adapt it to their own hook signature — the same split as
// `createProductAnalyticsClient`, which takes the SDK rather than importing it.
// Returns the event so a caller can pass it straight through; never drops one,
// because a dropped event is a silently missing funnel step.
export const redactAnalyticsEvent = ({
  event,
}: IRedactAnalyticsEventProps): IAnalyticsEventEnvelope | null => {
  if (!event) return event;

  if (event.properties) {
    scrubAnalyticsProperties({ properties: event.properties });
    scrubNestedPropertyBags({ holder: event.properties });
  }

  for (const bagKey of TOP_LEVEL_PROPERTY_BAGS) {
    const bag = event[bagKey];
    if (bag) {
      scrubAnalyticsProperties({ properties: bag });
    }
  }

  return event;
};
